import React, { useRef, useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as moment from 'moment';
import ContentContainer from '../../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../commonComponents/pageLayout/header/TopicBanner';
import Title from '../../../commonComponents/Title';
import {
  compose,
  COURSE_PROVIDER,
  COURSE_STATUS,
} from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import Select from '../../../commonComponents/forms/inputs/Select';
import DateRange from '../../../commonComponents/forms/inputs/DateRange';
import ReportCounterTotal from './counters/ReportCounterTotal';
import TextFilter from '../../../commonComponents/lists/filters/TextFilter';
import List from '../../../commonComponents/lists/List';
import TeamProp from '../../../commonComponents/propTypes/teamProp';
import Loader from '../../../services/loader';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import Notifications from '../../../services/notifications';
import CustomRadio from '../../../commonComponents/forms/inputs/CustomRadio';
import CertificateZipList from '../../../commonComponents/certificates/CertificateZipList';
import ReportTabBtns from '../tab/ReportTabBtns';
import { REPORT_TAB_TYPE } from '../propTypes/reportTabProp';

function ReportAcademyScorm({ t, _csrf, teams }) {
  const tableId = 'scormUsersList';
  const tableEl = useRef(null);
  const loader = useRef(new Loader('#scormReportsLayout'));
  const defaultTeam = teams.length > 0 ? teams[0]._id : null;

  const [isLoading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    team: defaultTeam,
    startDate: null,
    endDate: null,
    course: null,
    showInvited: false,
    showUnStarted: false,
  });
  const shouldShowListOfCertificates = filters.course;
  const [globalTotalValues, setGlobalTotalValues] = useState({
    courses: 0,
    users: 0,
    finished: 0,
  });
  const [query, setQuery] = useState({ search: '' });

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const setSearch = value =>
    setQuery(prevState => ({ ...prevState, search: value }));

  const getGlobalTotalValues = params =>
    new Promise((resolve, reject) => {
      // Fetch info for global total values
      const paramsToAPI = params;
      if (params.course) {
        delete paramsToAPI.course;
      }
      axios
        .get(`/api/analytics/academy/scorm/course/${filters.course}/totals`, {
          params: paramsToAPI,
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
        })
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });

  const fetchInfo = params => {
    setLoading(true);
    Promise.all([getGlobalTotalValues(params)])
      .then(([globalStats]) => {
        setGlobalTotalValues(globalStats);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleGetResults = () => {
    const params = {};
    if (filters.team) {
      params.team = filters.team;
    }
    if (filters.startDate && filters.endDate) {
      params.startDate = filters.startDate;
      params.endDate = filters.endDate;
    }
    if (filters.course) {
      params.course = filters.course;
    }
    if (filters.course) {
      fetchInfo(params);
    }
  };

  useEffect(() => {
    handleGetResults();
  }, [filters]);

  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
      showInvited: false,
      showUnStarted: false,
    }));
  };

  const renderStatus = status => {
    if (!status) {
      return '-';
    }
    const r = {
      [COURSE_STATUS.UN_STARTED]: {
        classBadge: 'm-badge--danger',
        classTitle: 'm--font--danger',
      },
      [COURSE_STATUS.OPENED]: {
        classBadge: 'm-badge--warning',
        classTitle: 'm--font--warning',
      },
      [COURSE_STATUS.IN_PROGRESS]: {
        classBadge: 'm-badge--info',
        classTitle: 'm--font--info',
      },
      [COURSE_STATUS.FINISHED]: {
        classBadge: 'm-badge--primary',
        classTitle: 'm--font--primary',
      },
    };
    return renderToStaticMarkup(
      <div>
        <span className={`m-badge ${r[status].classBadge} m-badge--dot`} />
        &nbsp;
        <span className={`m--font-bold ${r[status].classTitle}`}>
          {t(`course.status.${status}`)}
        </span>
      </div>,
    );
  };

  const renderTotalTime = totalTime => {
    const duration = moment.duration(totalTime);
    let durationAsString = '';
    if (duration.hours() > 0) {
      durationAsString += `${duration.hours()} h,`;
    }
    if (duration.minutes() > 0) {
      durationAsString += `${duration.minutes()} m,`;
    }
    durationAsString += `${duration.seconds()} s`;
    return renderToStaticMarkup(<div>{durationAsString}</div>);
  };

  const renderCompletedDate = (completedDate, status) => {
    if (completedDate && status && status === COURSE_STATUS.FINISHED) {
      return moment(completedDate).format('YYYY-MM-DD');
    }
    return '-';
  };
  const columns = [
    {
      field: 'user',
      title: t('reports.scorm.table.user'),
      textAlign: 'left',
      sortable: false,
      template: ({ user }) =>
        renderToStaticMarkup(
          <UserColumn t={t} user={user} includeInvitedStatus />,
        ),
    },
    {
      field: 'email',
      title: t('reports.scorm.table.email'),
      textAlign: 'left',
      width: 200,
      sortable: false,
      template: ({ user }) => renderToStaticMarkup(<div>{user.email}</div>),
    },
    {
      field: 'status',
      title: t('reports.scorm.table.status'),
      textAlign: 'center',
      width: 100,
      sortable: false,
      template: ({ status }) => renderStatus(status),
    },
    {
      field: 'score',
      title: t('reports.scorm.table.score'),
      textAlign: 'center',
      width: 100,
      sortable: false,
      template: ({ score }) =>
        renderToStaticMarkup(<div>{score >= 0 ? score.toFixed(2) : 0}</div>),
    },
    {
      field: 'totalTime',
      title: t('reports.scorm.table.total_time'),
      textAlign: 'center',
      width: 100,
      sortable: false,
      template: ({ totalTime }) => renderTotalTime(totalTime),
    },
    {
      field: 'completedDate',
      title: t('reports.scorm.table.completed_date'),
      textAlign: 'center',
      width: 100,
      sortable: false,
      template: ({ completedDate, status }) =>
        renderToStaticMarkup(
          <div>{renderCompletedDate(completedDate, status)}</div>,
        ),
    },
  ];

  // Challenges State
  const [coursesList, setCoursesList] = useState([]);
  /** API REQUESTS */
  const getCourses = () => {
    setLoading(true);
    const params = {
      teams: filters.team,
      isFromExternalProvider: true,
    };
    axios
      .get('/api/course/list/managers', {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setCoursesList(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_failed_getting_data');
        setLoading(false);
      });
  };
  /** API REQUESTS */
  useEffect(() => {
    getCourses();
  }, [filters.team]);

  // Show Performance Report
  const [showScorm, setShowScorm] = useState(false);
  const showReport = () => {
    setShowScorm(true);
  };

  const handleFilterChange = field => value => {
    if (field === 'team') {
      setFilters({ team: value, course: null });
      setShowScorm(false);
    } else {
      setShowScorm(false);
      setFilters(prevState => ({ ...prevState, [field]: value }));
    }
  };

  const handleCheckbox = field => {
    setFilters(prevState => ({ ...prevState, [field]: !prevState[field] }));
  };

  const getExcelUrl = () => {
    const url = `/excel/analytics/academy/scorm/course/${filters.course}/user/report/xlsx?`;
    let teamQuery = '';
    let startQuery = '';
    let showInvitedQuery = '';
    let showUnStartedQuery = '';
    let endQuery = '';
    if (filters.team) {
      teamQuery = `&team=${filters.team}`;
    }
    if (filters.showInvited) {
      showInvitedQuery = `&showInvited=${filters.showInvited}`;
    }
    if (filters.showUnStarted) {
      showUnStartedQuery = `&showUnStarted=${filters.showUnStarted}`;
    }
    if (filters.startDate && filters.endDate) {
      startQuery = `&startDate=${filters.startDate}`;
      endQuery = `&endDate=${filters.endDate}`;
    }
    return `${url}${teamQuery}${showInvitedQuery}${showUnStartedQuery}${startQuery}${endQuery}`;
  };
  return (
    <Title title={t('myReports')}>
      <ContentContainer>
        <SubHeader title={t('myReports')} />
        <TopicBanner />
        <div className="m-content">
          <div className="full-page-container-with-subheader">
            <div id="scormReportsLayout">
              <div className="row">
                <div className="col-lg-12">
                  <div className="m-portlet m-portlet--mobile">
                    <div className="report-filters-content">
                      <ReportTabBtns tabActive={REPORT_TAB_TYPE.SCORM} />
                      <div className="row">
                        <div className="row col-lg-12 report-filters-inputs">
                          <div className="col-lg-3">
                            <Select
                              id="filerTeam"
                              name={t('reports.scorm.filter.team')}
                              placeholder={t(
                                'reports.scorm.filter.team.placeholder',
                              )}
                              type="select2"
                              onChange={handleFilterChange('team')}
                              value={filters.team}
                              className="form-control m-input"
                              multiple={false}
                              items={teams.map(te => ({
                                id: te._id,
                                text: te.name,
                              }))}
                            />
                          </div>
                          <div className="col-lg-3">
                            <Select
                              id="filterCourse"
                              name="filterCourse"
                              placeholder={t('team.report.scorm.select')}
                              type="select2"
                              onChange={handleFilterChange('course')}
                              value={filters.course}
                              className="form-control m-input"
                              multiple={false}
                              items={coursesList.map(c => ({
                                id: c._id,
                                text: c.name,
                              }))}
                            />
                          </div>
                          <div className="col-lg-3">
                            <DateRange
                              id="filerDate"
                              placeholder={t(
                                'reports.scorm.filter.date.placeholder',
                              )}
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              onChange={({ startDate, endDate }) => {
                                handleDateChange(startDate, endDate);
                              }}
                              onClear={() => {
                                handleDateChange(null, null);
                              }}
                              required={false}
                            />
                          </div>
                          <div className="col-lg-3">
                            <button
                              type="button"
                              onClick={showReport}
                              disabled={filters.course === null}
                              className="btn btn-dynamic"
                            >
                              {t('reports.scorm.see_results')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {showScorm && filters.course && (
                <>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="m-portlet m-portlet--mobile">
                        <div className="row">
                          <div className="col-lg-4">
                            <ReportCounterTotal
                              title={t('reports.scorm.total_users')}
                              description={t(
                                'reports.scorm.total_courses_users_description',
                              )}
                              value={globalTotalValues.totalUsers}
                            />
                          </div>
                          <div className="col-lg-4">
                            <ReportCounterTotal
                              title={t('reports.courses.totals.users_progress')}
                              description={t(
                                'reports.courses.totals.users_progress_in_course',
                              )}
                              value={globalTotalValues.totalInProgress}
                            />
                          </div>
                          <div className="col-lg-4">
                            <ReportCounterTotal
                              title={t(
                                'reports.courses.totals.users_completed',
                              )}
                              description={t(
                                'reports.courses.totals.users_completed_in_course',
                              )}
                              value={globalTotalValues.totalCompleted}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="m-portlet m-portlet--mobile">
                        <div className="m-portlet__head">
                          <div className="m-portlet__head-caption">
                            <div className="m-portlet__head-title">
                              <h3 className="m-portlet__head-text m--font-primary">
                                {t('reports.scorm.users_per_course')}
                              </h3>
                            </div>
                          </div>
                          <div className="m-portlet__head-tools">
                            <div className="report-filters-checkbox">
                              <div
                                className={
                                  filters.startDate || filters.endDate
                                    ? 'disabled-checkbox'
                                    : ''
                                }
                              >
                                <CustomRadio
                                  id="showUnStarted"
                                  label={t('reports.scorm.showUnStarted')}
                                  checked={filters.showUnStarted}
                                  onChange={() =>
                                    handleCheckbox('showUnStarted')
                                  }
                                  isCheckbox
                                  disabled={
                                    filters.startDate || filters.endDate
                                  }
                                />
                              </div>
                              <div
                                className={
                                  filters.startDate || filters.endDate
                                    ? 'disabled-checkbox'
                                    : ''
                                }
                              >
                                <CustomRadio
                                  id="showInvited"
                                  label={t('reports.scorm.showInvited')}
                                  checked={filters.showInvited}
                                  onClick={() => handleCheckbox('showInvited')}
                                  isCheckbox
                                  disabled={
                                    filters.startDate || filters.endDate
                                  }
                                />
                              </div>
                            </div>
                            <a
                              href={getExcelUrl()}
                              className="btn btn-brand btn-sm"
                            >
                              {t('export.excel')}
                            </a>
                          </div>
                        </div>
                        <div className="m-portlet__body">
                          <div className="m-form m-form--label-align-right m--margin-bottom-30">
                            <div className="row align-items-center">
                              <div className="col-xl-6 col-md-6 order-2 order-xl-1">
                                <div className="form-group m-form__group row align-items-center">
                                  <div className="col-md-10">
                                    <TextFilter
                                      onChange={setSearch}
                                      placeholder={t('search')}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <List
                            ref={tableEl}
                            columns={columns}
                            id={tableId}
                            dataSourceURL={`/table/analytics/academy/scorm/course/${filters.course}/user/report/list`}
                            dataSourceHeaders={{
                              'Accept-Version': '1.0.0',
                              'X-Gamification-API': 'Games',
                            }}
                            query={{ ...filters, ...query }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12 team-report-certificates">
                      {shouldShowListOfCertificates && (
                        <CertificateZipList
                          filters={{
                            ...filters,
                            type: 'courses',
                            typeCourses: COURSE_PROVIDER.SCORM,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

ReportAcademyScorm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape(TeamProp.propType)),
};

ReportAcademyScorm.defaultProps = {
  teams: [],
};

export default compose(translate)(ReportAcademyScorm);
